import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import type { AxiosError } from "axios";
import KalixLogo from "../../components/svgElements/KalixLogo";
import InputWrapper from "../../components/formElements/InputWrapper";
import Textbox from "../../components/formElements/Textbox";
import Button from "../../components/formElements/Button";
import { ROUTES } from "../../constants/routes";
import { verifyCode, forgotPassword } from "../../services/auth";
import { showErrorToast, showSuccessToast } from "../../utils/helper";
import "./Auth.scss";
import styles from "./Verification.module.css";

interface IVerificationForm {
  code: string;
}

const currentYear = new Date().getFullYear();

const Verification = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const email = location.state?.email;
  const otpSent = location.state?.otpSent;

  useEffect(() => {
    if (otpSent) {
      setCountdown(30);
    }
  }, [otpSent]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IVerificationForm>({
    mode: "onSubmit",
    defaultValues: {
      code: "",
    },
  });

  const onSubmit = async (data: IVerificationForm) => {
    if (!email) {
      showErrorToast("Email not found. Please start from forgot password.");
      navigate(ROUTES.FORGOT_PASSWORD);
      return;
    }

    try {
      setIsLoading(true);
      const response = await verifyCode(email, data.code);

      if (response.success) {
        showSuccessToast(response.message || t("auth.verificationSuccess"));
        navigate(ROUTES.RESET_PASSWORD, {
          state: { email, code: data.code },
        });
      } else {
        showErrorToast(response.message || t("auth.verificationFailed"));
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const errorMessage =
        axiosError?.response?.data?.message ||
        axiosError?.message ||
        t("auth.verificationError");
      showErrorToast(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      showErrorToast("Email not found. Please start from forgot password.");
      navigate(ROUTES.FORGOT_PASSWORD);
      return;
    }

    try {
      setIsResending(true);
      const response = await forgotPassword(email);

      if (response.success) {
        showSuccessToast(t("auth.verificationCodeResent"));
        setCountdown(30);
      } else {
        showErrorToast(response.message || "Failed to resend code.");
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const errorMessage =
        axiosError?.response?.data?.message ||
        axiosError?.message ||
        "An error occurred while resending code.";
      showErrorToast(errorMessage);
    } finally {
      setIsResending(false);
    }
  };
  return (
    <div className="userauth-main">
      <div className="row g-3">
        <div className="col-md-7">
          <div className="userauth-bg" />
        </div>

        <div className="col-md-5">
          <div className="userauth-form">
            <div className="userauth-logo">
              <KalixLogo />
            </div>
            <div className="userauth-form-content">
              <h2 className="text-center">{t("auth.verification")}</h2>
              <p className="text-center">{t("auth.verificationDescription")}</p>
              <form onSubmit={handleSubmit(onSubmit)}>
                <InputWrapper>
                  <InputWrapper.Label required>
                    {t("auth.verificationCode")}
                  </InputWrapper.Label>
                  <Textbox
                    type="text"
                    name="code"
                    placeholder={t("auth.enterVerificationCode")}
                    control={control}
                    className="form-control"
                    maxLength={4}
                    rules={{
                      required: "Verification code is required",
                      pattern: {
                        value: /^\d{4}$/,
                        message: "Code must be exactly 4 digits",
                      },
                      minLength: {
                        value: 4,
                        message: "Code must be exactly 4 digits",
                      },
                      maxLength: {
                        value: 4,
                        message: "Code must be exactly 4 digits",
                      },
                    }}
                  />
                  {errors.code && (
                    <span className="auth-msg error">
                      {errors.code.message}
                    </span>
                  )}
                </InputWrapper>

                <Button
                  type="submit"
                  className="primary-btn rounded-md w-100"
                  disabled={isLoading}
                >
                  {isLoading ? t("common.loading") : t("auth.verify")}
                </Button>

                <div className="button-align justify-content-center mt-5">
                  <p className="color-dark">
                    {t("auth.didNotReceiveCode")}{" "}
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={isResending || countdown > 0}
                      className={`color-primary ${styles.resendButton} ${countdown > 0 || isResending ? styles.resendButtonDisabled : ''}`}
                    >
                      {isResending
                        ? "Sending..."
                        : countdown > 0
                          ? `${t("auth.resendCode")} (${countdown}s)`
                          : t("auth.resendCode")}
                    </button>
                  </p>
                </div>
              </form>
            </div>
            <p className="copyright-text">
              {t("auth.copyright", { year: currentYear })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Verification;
